// SubmissionsBulkActions — the selection bar's "More" menu, styled after the
// Groups tab's Move-to-section dropdown. PDF report, Download attachments and
// PDF versions target ONE submission — disabled with a tooltip while the
// selection holds more. Export CSV starts the server-side export job; Archive (or
// Restore on the archived list) and Delete fire POST submissions/bulk-action
// through the list's callbacks; Delete goes through the page's confirm first.
// Every item is live. Presentational — SubmissionsList owns the flows.
// Export CSV / Archive / Delete are OPTIONAL callbacks: the list passes
// `undefined` when the `reports` verb is missing and the item doesn't render —
// absence means hidden. PDF report + PDF versions are read items and always
// present, so the menu never empties.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Icon } from '@/components/common';

interface SubmissionsBulkActionsProps {
  /** Open the pdf render of the single selected submission. */
  onPdfReport: () => void;
  /** Open the version-history modal of the single selected submission. */
  onPdfVersions: () => void;
  /** Start the attachments export job of the single selected submission —
   *  export-manager family, so verb-gated like Export CSV. Absent = hidden. */
  onDownloadAttachments?: () => void;
  /** True while the selection isn't exactly one row — disables the
   *  single-selection items (PDF report, Download attachments, PDF versions). */
  pdfDisabled: boolean;
  /** The archived list is showing — the Archive item reads Restore instead. */
  archived: boolean;
  /** Start the CSV export job over the selection. Absent = hidden. */
  onExportCsv?: () => void;
  /** Archive on the active list, restore on the archived one. Absent = hidden. */
  onArchive?: () => void;
  /** Opens the delete confirmation. Absent = hidden. */
  onDelete?: () => void;
}

const SubmissionsBulkActions = ({
  onPdfReport,
  onPdfVersions,
  onDownloadAttachments,
  pdfDisabled,
  archived,
  onExportCsv,
  onArchive,
  onDelete,
}: SubmissionsBulkActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Close the menu, then run the action (which opens its overlay/toast).
  const pick = (run: () => void) => () => {
    setOpen(false);
    run();
  };

  const item = (
    label: string,
    icon: string,
    onClick: () => void,
    opts: { danger?: boolean } = {},
  ) => (
    <button
      type='button'
      className={`app-dropdown__item${opts.danger ? ' app-dropdown__item--danger' : ''}`}
      role='menuitem'
      onClick={pick(onClick)}
    >
      <Icon name={icon} size={15} />
      <span className='app-dropdown__item-label'>{label}</span>
    </button>
  );

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='start'
      width={240}
      portal
      trigger={
        <Button
          variant='ghost'
          size='sm'
          iconStart='ellipsis'
          iconEnd={open ? 'chevron-up' : 'chevron-down'}
          aria-haspopup='menu'
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {t('text_more')}
        </Button>
      }
    >
      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        disabled={pdfDisabled}
        aria-disabled={pdfDisabled}
        title={pdfDisabled ? t('submissions_bulk_pdf_single') : undefined}
        onClick={pick(onPdfReport)}
      >
        <Icon name='eye' size={15} />
        <span className='app-dropdown__item-label'>{t('report_view_pdf')}</span>
      </button>
      {onDownloadAttachments && (
        <button
          type='button'
          className='app-dropdown__item'
          role='menuitem'
          disabled={pdfDisabled}
          aria-disabled={pdfDisabled}
          title={pdfDisabled ? t('submissions_bulk_pdf_single') : undefined}
          onClick={pick(onDownloadAttachments)}
        >
          <Icon name='folder-down' size={15} />
          <span className='app-dropdown__item-label'>
            {t('submissions_bulk_download_attachments')}
          </span>
        </button>
      )}
      {/* `history` — the old lucide (^1.23.0) has no rotate-ccw-clock; this is
          the same clock-with-ccw-arrow drawing. Single-selection like PDF
          report: the version modal's meta strip is one row's identity. */}
      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        disabled={pdfDisabled}
        aria-disabled={pdfDisabled}
        title={pdfDisabled ? t('submissions_bulk_pdf_single') : undefined}
        onClick={pick(onPdfVersions)}
      >
        <Icon name='history' size={15} />
        <span className='app-dropdown__item-label'>{t('submissions_bulk_pdf_versions')}</span>
      </button>
      {onExportCsv && item(t('submissions_bulk_export_csv'), 'download', onExportCsv)}
      {onArchive &&
        (archived
          ? item(t('action_restore'), 'archive-restore', onArchive)
          : item(t('forms_action_archive'), 'archive', onArchive))}
      {onDelete && item(t('forms_action_delete'), 'trash-2', onDelete, { danger: true })}
    </Dropdown>
  );
};

export default SubmissionsBulkActions;
